import { createApi } from "@reduxjs/toolkit/query/react";
import { AD, TagId, TagType } from "../../configs";
import {
  Ad,
  AdDetails,
  HttpMethod,
  PaginatedResponse,
  PaginationQuery,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

export interface SearchByCityId extends PaginationQuery {
  cityId?: string;
}

const buildAdFormData = (body: { image?: File; cities: string[] }) => {
  const formData = new FormData();

  body.cities.forEach((city) => {
    formData.append("cities[]", city);
  });

  if (body.image) {
    formData.append("image", body.image);
  }

  return formData;
};

const adApi = createApi({
  reducerPath: AD.reducerPath,
  baseQuery: createBaseQuery(AD.baseUrl),
  tagTypes: AD.tagTypes,
  endpoints(builder) {
    return {
      toggleAdIsActiveStatus: builder.mutation<
        void,
        { adId: string; isActive: boolean }
      >({
        query: ({ adId, isActive }) => ({
          url: `/${adId}/active`,
          method: HttpMethod.PATCH,
          params: { isActive },
        }),
        invalidatesTags: () => [{ type: TagType.Ad, id: TagId.LIST }],
      }),

      fetchAds: builder.query<PaginatedResponse<Ad>, SearchByCityId>({
        query: (params) => ({
          url: "/",
          method: HttpMethod.GET,
          params,
        }),
        providesTags: () => [{ type: TagType.Ad, id: TagId.LIST }],
      }),

      fetchAdDetails: builder.query<AdDetails, string>({
        query: (adId) => ({
          url: `/${adId}`,
          method: HttpMethod.GET,
        }),
        providesTags: (_result, _error, adId) => [
          { type: TagType.Ad, id: adId },
        ],
      }),

      createAd: builder.mutation<AdDetails, { image?: File; cities: string[] }>(
        {
          query: (body) => ({
            url: "",
            method: HttpMethod.POST,
            body: buildAdFormData(body),
          }),
          invalidatesTags: () => [
            { type: TagType.Ad, id: TagId.ALL_LIST },
            { type: TagType.Ad, id: TagId.LIST },
          ],
        }
      ),

      updateAd: builder.mutation<
        void,
        { adId: string; body: { image?: File; cities: string[] } }
      >({
        query: ({ adId, body }) => ({
          url: `/${adId}`,
          method: HttpMethod.PUT,
          body: buildAdFormData(body),
        }),
        invalidatesTags: (_result, _error, { adId }) => [
          { type: TagType.Ad, id: TagId.LIST },
          { type: TagType.Ad, id: adId },
        ],
      }),

      deleteAd: builder.mutation<void, string>({
        query: (adId) => ({
          url: `/${adId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, adId) => [
          { type: TagType.Ad, id: TagId.LIST },
          { type: TagType.Ad, id: adId },
        ],
      }),
    };
  },
});

export const {
  useFetchAdsQuery,
  useFetchAdDetailsQuery,
  useCreateAdMutation,
  useUpdateAdMutation,
  useToggleAdIsActiveStatusMutation,
  useDeleteAdMutation,
} = adApi;

export { adApi };
